import { useCurrentUser, useResetPassword, useSessionId, useSessions } from '@ctcells/api'
import { useState } from 'react'

import { dataSourceFor } from '@ctcells/core'
import { errorMessage, t } from '@ctcells/i18n'

import { useMinuteTick } from '../monitoring/minuteTick.ts'
import { useScreenAccess } from '../screenAccess.ts'

import { AccountScreen } from './AccountScreen.tsx'

/**
 * 「내 계정」 컨테이너.
 *
 * 그동안 이 화면만 **컨테이너가 없어서** 라우트가 화면을 직접 걸었고, `sessions` 도메인이
 * `live` 인데도 세션 표가 항상 fixture(`SESSIONS` — `usr-eric` 의 가짜 세션 4건)였다.
 * 사이드바 사용자 영역과 **같은 세션 출처**를 쓰게 맞춘 것이 이 파일이다.
 *
 * 🔴 **갈리는 층은 `dataSourceFor('sessions')` 하나다.** demo 경로는 네트워크 훅을 타지 않는다.
 * 🔴 **화면은 0줄도 안 바뀐다.** 훅을 부르고 props 로 내려줄 뿐이다.
 *
 * ⚠ **현재 사용자 조회 경로가 생겼다** — `GET /auth/me` 가 있다(2026-09-26 확인 · admin·operator·viewer 3역할 · 응답 `{ user_id, role, operations[] }` — `operations` 는 그 역할이 부를 수 있는 operationId 목록).
 *   아직 갈아타지 않았다(eric 결정거리). ⚠ 응답에 `username`·`name` 이 없어 `User` 전체를 대신하지는 못한다.
 *   지금 내 `user_id` 의 출처는 로그인 응답의 `TokenResponse.user` 뿐이고, 새로고침 뒤에는
 *   `sessionStorage` 에 저장해 둔 그 값을 `restoreSession()` 이 되얹는다.
 *   **그 값마저 없으면**(저장소를 비웠거나 다른 탭에서 시작) 사용자를 모르므로
 *   `GET /sessions` 를 **아예 부르지 않는다**(`skip`) — 남의 세션 63건을 「내 세션」으로
 *   그리는 것이 최악이다. 그때 표는 빈 상태 문구(`state.empty.sessions`)로 떨어진다.
 *
 * ⚠ `Session` 에 **「이 기기」 표시가 없다.** 세션 스토어가 들고 있는 `session_id`
 *   (로그인 응답 `TokenResponse.session_id`)와 맞춰 보는 것이 유일한 방법이다.
 *   ⚠ `Session.client`(접속 기기)는 실물이 줄 때도 있고 안 줄 때도 있다(보고 대상 K) — 열이 빈다.
 *
 * 🔴 **주기 폴링 없다.** 스냅샷 1회 + 구독 무효화가 전부다(DP-01).
 *
 * 경계 다섯
 * | 로딩 | 세션 조회 중 — 표의 빈 문구가 잠깐 뜬다(행이 0인 것과 같은 그림이라 별도 스켈레톤 없음) |
 * | 에러 | 🔴 **세션 표 구역에만** 사유를 적는다. 비밀번호 구역은 그대로 산다 — 그 경로는        |
 * |      | 전 역할 self-service 인데 admin 전용 조회 하나 때문에 막히면 안 된다(2026-09-21)      |
 * | 빈   | 0행이면 표가 「세션 없음」을 그린다                                                   |
 * | 권한 | `useScreenAccess()` 의 세션 권한. 버튼 비활성은 화면이 한다                           |
 * | 끊김 | `access.controlDisabled` → 화면 상단 「연결 끊김」 띠(`OfflineNotice`)                |
 */

/** 한 번에 받는 상한. 서버 최대 500 아래다. 내 세션은 몇 건이라 넉넉하다 */
const SESSION_LIMIT = 200

export function AccountContainer() {
  return dataSourceFor('sessions') === 'demo' ? <AccountDemo /> : <AccountLive />
}

/** demo 경로는 props 를 안 넘긴다 = 화면 기본값(fixture `SESSIONS`)이 그대로 산다 */
function AccountDemo() {
  const access = useScreenAccess()
  return <AccountScreen access={access} />
}

function AccountLive() {
  const access = useScreenAccess()
  const user = useCurrentUser()
  const sessionId = useSessionId()
  // 만료된 세션을 거르는 「지금」 — 분 단위 시계 읽기(서버에 묻지 않는다)
  const now = useMinuteTick()

  const sessions = useSessions(
    user === null
      ? { limit: SESSION_LIMIT, skip: true }
      : { limit: SESSION_LIMIT, user_id: user.user_id },
  )

  /*
   * 🔴 **세션 표는 부가 자료다. 실패해도 화면을 덮지 않는다**(2026-09-21).
   *
   * ⚠ **실제 사고:** 여기에 `if (sessions.isError) return <ScreenState …>` 가 있었다.
   *   `GET /sessions` 는 **admin 전용**(`views/sessions.py` `ALLOWED_ROLES = {"admin"}`)인데
   *   「내 계정」은 `ACCOUNT_NAV_ITEM.permission = 'monitor'` 라 **세 역할 모두 문이 열린다.**
   *   그래서 operator·viewer 가 이 화면에 들어오면 403 하나가 화면 전체를 덮었고,
   *   **자기 비밀번호를 바꿀 수단이 사라졌다** — 그 경로(`PUT /users/{id}/password`)는
   *   `require_roles(ROLES)` 로 **전 역할 self-service** 인데도.
   * 🔴 모니터링의 `/samples` 와 **같은 병**이다. 주 자료(내 계정 = 내 자격)는 세션 목록이 아니다.
   */
  const sessionsNotice = sessions.isPending
    ? t('state.loading')
    : sessions.isError
      ? errorMessage(sessions.error)
      : undefined

  // 🔴 서버 필터를 믿고 끝내지 않는다 — 「내 계정」에 남의 세션이 한 줄이라도 섞이면 안 되고,
  //    그 판단 근거는 서버가 준 `user_id` 비교 하나다(프론트가 추론하는 것이 아니다).
  const mine = (sessions.data?.items ?? []).filter((session) => session.user_id === user?.user_id)

  /*
   * 비밀번호 변경 — 본인 재설정이라 `currentPassword` 를 싣는다(서버가 없으면 422 `USER_VALIDATION_FAIL`).
   * 🔴 성공 시각은 **응답 뒤에** 적는다 — 화면은 이 값이 바뀌는 것으로 창을 닫는다(낙관적 갱신 없음).
   */
  const reset = useResetPassword()
  const [passwordChangedAt, setPasswordChangedAt] = useState<string | undefined>(undefined)

  return (
    <AccountScreen
      access={access}
      changingPassword={reset.isPending}
      now={now}
      passwordChangedAt={passwordChangedAt}
      sessions={mine}
      sessionsNotice={sessionsNotice}
      {...(sessionId === null ? {} : { currentSessionId: sessionId })}
      {...(reset.error === null ? {} : { changePasswordError: errorMessage(reset.error) })}
      onResetPasswordError={() => reset.reset()}
      {...(user === null
        ? {}
        : {
            onChangePassword: (currentPassword: string, nextPassword: string) => {
              reset.mutate(
                { userId: user.user_id, newPassword: nextPassword, currentPassword },
                { onSuccess: () => setPasswordChangedAt(new Date().toISOString()) },
              )
            },
          })}
    />
  )
}
